"use client";

import React, { createContext, useContext, useMemo } from "react";
import useLocale from "./use-locale";
import useCurrency from "./use-currency";

type FormatContextType = {
  formatNumber: (
    val: number,
    numDigits?: number,
    minimumFractionDigits?: number
  ) => string;
  calculateCurrencyConvertion: (
    value: number | undefined,
    numDigits: number,
    displaySymbol?: boolean
  ) => string;
  currentLocale: string | undefined;
  globalCurrency: string;
  globalCurrencySymbol: string;
};

const FormatContext = createContext<FormatContextType | null>(null);

export function FormatProvider({ children }: { children: React.ReactNode }) {
  const { currentLocale } = useLocale();
  const { calculateCurrencyConvertion, globalCurrency, globalCurrencySymbol } =
    useCurrency();

  const value = useMemo(() => {
    const formatNumber = (
      val: number,
      numDigits: number = 0,
      minimumFractionDigits?: number
    ): string => {
      const defaultVal: number = 0.0;
      if (!val) val = defaultVal;

      const formatted = val.toLocaleString(currentLocale, {
        maximumFractionDigits: numDigits,
        minimumFractionDigits: minimumFractionDigits ?? numDigits,
      });

      // Check if value rounds to 0 but is actually non-zero
      const roundedValue =
        Math.round(val * Math.pow(10, numDigits)) / Math.pow(10, numDigits);
      if (val !== 0 && roundedValue === 0) {
        return `~${formatted}`;
      }

      return formatted;
    };

    return {
      formatNumber,
      calculateCurrencyConvertion,
      currentLocale,
      globalCurrency,
      globalCurrencySymbol,
    };
  }, [
    currentLocale,
    calculateCurrencyConvertion,
    globalCurrency,
    globalCurrencySymbol,
  ]);

  return (
    <FormatContext.Provider value={value}>{children}</FormatContext.Provider>
  );
}

/**
 * Hook to access format functions from context.
 * Much more performant than useFormat() or useCurrency() for components rendered many times (like table cells).
 */
export function useFormatContext(): FormatContextType {
  const context = useContext(FormatContext);
  if (!context) {
    throw new Error("useFormatContext must be used within a FormatProvider");
  }
  return context;
}
